import { flow, makeObservable, override } from 'mobx';
import { RootStore } from './RootStore';
import { Organization } from './models';
import { DashboardData, IDashboardData } from './models/DashboardData';
import { DataStore } from './DataStore';
import { IServerOrganization, ServerOrganization } from './models/server/ServerOrganization';
import { IRequestConfig } from './models/RequestQueue';
import { IPortalForm } from '../components/AccountSettings/Pages/Organization/CustomPortal';
import { IServerReportData, ReportData } from './models/ReportData';
import { DateFormatter } from '../logic/DateFormatter';
import { IAdministratorOverview } from './models/interfaces/IAdministratorOverview';
import { IAPIGetRequestParameters } from './models/APIGetRequestParameters';

enum OrganizationRequestType {
    GetDashboardData = 'get-dashboard-data',
    GetInformation = 'get-org-information',
    GetAdmins = 'get-admins',
    GetOpportunities = 'get-org-opportunities',
    GetOpportunitiesOverview = 'get-org-opportunities-overview',
    GetReportData = 'get-report-data',
    FindVolunteersWithEmail = 'find-volunteers-with-email',
    PatchOrganization = 'patch-organization',
    PatchPortal = 'patch-portal',
    DeleteAdmins = 'delete-admin'
}

/*** Maintains the data for a single organization ***/

export class OrganizationStore extends DataStore<OrganizationRequestType> {

    organization = new Organization(-1);

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this);
    }

    /*************** API Methods ***************/

    /*  ------------ Request URLs ------------  */

    @override get requestURLs() {
        const opportunitiesAPI = '/api/opportunities';
        const organizationsAPI = '/api/organizations'
        return {
            [OrganizationRequestType.GetInformation]: (data: { idOrSlug: number | string }) => `${organizationsAPI}/organization/${data.idOrSlug}`,
            [OrganizationRequestType.GetOpportunities]: (data: { id: number }) => `${opportunitiesAPI}/organization/${data.id}`,
            [OrganizationRequestType.GetAdmins]: (data: any, params: { idOrSlug: number | string }) => `${organizationsAPI}/${params.idOrSlug}/admins`,
            [OrganizationRequestType.GetOpportunitiesOverview]: (data: { id: number }) => `${opportunitiesAPI}/overview/${data.id}`,
            [OrganizationRequestType.GetDashboardData]: `${organizationsAPI}/dashboard`,
            [OrganizationRequestType.GetReportData]: (data: { id: number }) => `${organizationsAPI}/${data.id}/reports`,
            [OrganizationRequestType.FindVolunteersWithEmail]: `${organizationsAPI}/find-volunteers-with-email`,
            [OrganizationRequestType.PatchOrganization]: (data: any, params: { organizationId: number }) => `${organizationsAPI}/${params.organizationId}`,
            [OrganizationRequestType.PatchPortal]: (data: any, params: { organizationId: number }) => `${organizationsAPI}/${params.organizationId}/portal`,
            [OrganizationRequestType.DeleteAdmins]: (data: any, params: { organizationId: number }) => `${organizationsAPI}/${params.organizationId}/admins`,
        };
    }

    /*  ------------ GET Methods ------------  */

    @override get getRequests() {
        return [
            OrganizationRequestType.GetInformation,
            OrganizationRequestType.GetOpportunities,
            OrganizationRequestType.GetAdmins,
            OrganizationRequestType.GetOpportunitiesOverview,
            OrganizationRequestType.GetDashboardData,
            OrganizationRequestType.FindVolunteersWithEmail,
            OrganizationRequestType.GetReportData
        ];
    }

    async getDashboardData(id: number) {
        const response = await this.makeRequest<IDashboardData>(OrganizationRequestType.GetDashboardData, { id });
        if (!response) return;
        return new DashboardData(response);
    };

    async loadOrgInfo(idOrSlug: number | string) {
        const response = await this.requestOrgInfo(idOrSlug);
        if (!response) return;
        this.organization.onDataLoaded(response);
    };

    async getOrgInfo(idOrSlug: number | string) {
        const response = await this.requestOrgInfo(idOrSlug);
        if (!response || !('organization' in response)) return;
        return new ServerOrganization(response.organization).deserialize();
    };

    async getAdmins(idOrSlug: number | string, parameters: IAPIGetRequestParameters<keyof IAdministratorOverview>) {
        this.cancelInFlightRequests(OrganizationRequestType.GetAdmins);
        const response = await this.makeRequest<{ total: number; results: IAdministratorOverview[]; }>(
            OrganizationRequestType.GetAdmins,
            { limit: parameters.limit, offset: parameters.offset },
            { idOrSlug }
        );
        if (!response) return;
        return response;
    };

    isPortalSlugUnique = flow(function* (this: OrganizationStore, slug: string) {
        return yield this.getOrgInfo(slug).then((orgUsingSlug) => {
            const currentOrg = this.rootStore.userStore.user.organization;
            if (!orgUsingSlug) return true;
            return orgUsingSlug.id === currentOrg?.id;
        })
    });

    async requestOrgInfo(idOrSlug: number | string) {
        return await this.makeRequest<{ organization: IServerOrganization }>(
            OrganizationRequestType.GetInformation,
            { idOrSlug }
        );
    }

    async getOpportunitiesForOrganization(id: number, limit: number, offset: number) {
        const response = await this.makeRequest<{ organization: IServerOrganization }>(
            OrganizationRequestType.GetOpportunities,
            { id, limit, offset }
        );
        if (!response) return;
        this.organization.onDataLoaded(response);
    };

    async getOrganizationOpportunitiesOverview(id: number, offset: number, limit: number) {
        const response = await this.makeRequest<{ organization: IServerOrganization }>(
            OrganizationRequestType.GetOpportunitiesOverview,
            { id, limit, offset }
        );
        if (!response) return;
        this.organization.onDataLoaded(response);
    };

    findVolunteersWithEmail = flow(function* (this: OrganizationStore, email: string) {
        const orgId = this.rootStore.userStore.user.organization?.id;
        if (orgId) {
            return yield this.makeRequest<boolean>(OrganizationRequestType.FindVolunteersWithEmail, { id: orgId, email: email.trim() });
        }
    });

    async getReportData(id: number, startDate: Date, endDate: Date) {
        const start = DateFormatter.getDateStringWithoutTimestamp(startDate);
        const end = DateFormatter.getDateStringWithoutTimestamp(endDate);
        const response = await this.makeRequest<IServerReportData>(OrganizationRequestType.GetReportData, { id, startDate: start, endDate: end });
        if (!response) return;
        return new ReportData(response);
    };

    /*  ------------ PATCH Methods ------------  */

    @override get patchRequests() {
        return [OrganizationRequestType.PatchPortal, OrganizationRequestType.PatchOrganization];
    }

    async patchOrganization(organizationPatch: Partial<IServerOrganization>) {//organization: Partial<Organization>) {
        // const serializedOrg = organization.serialize();
        const serializedData = organizationPatch;
        const userOrg = this.rootStore.userStore.user.organization;
        if (!userOrg) return;
        const response = await this.makeRequest<IServerOrganization>(
            OrganizationRequestType.PatchOrganization,
            { organization: serializedData },
            { organizationId: userOrg.id }
        );
        if (!response) return;
        userOrg?.onDataLoaded({ organization: response });
    };

    async patchPortal(organization: Organization, portalData: Partial<IPortalForm>) {
        const formData = new FormData();
        (Object.keys(portalData) as (keyof IPortalForm)[]).forEach(key => {
            if (key !== 'logo' && portalData[key]) {
                formData.set(key, portalData[key]!);
            }
        })
        // Add the logo last
        if (portalData.logo) {
            formData.set('file', portalData.logo);
        }

        const response = await this.makeRequest<{ organization: IServerOrganization }>(
            OrganizationRequestType.PatchPortal,
            formData,
            {
                organizationId: organization.id
            }
        );
        if (!response) return;
        organization.onDataLoaded(response);
    };

    /*  ------------ DELETE Methods ------------  */

    @override get deleteRequests() {
        return [OrganizationRequestType.DeleteAdmins];
    }

    deleteAdmins = async (emails: string[]) => {
        const deletingSelf = emails.findIndex(email => email === this.rootStore.userStore.user.email) !== -1;
        const response = await this.makeRequest<{ success: boolean } | { error: string }>(
            OrganizationRequestType.DeleteAdmins,
            { emails },
            { organizationId: this.rootStore.userStore.user.organization?.id }
        );
        if (!response || 'error' in response) throw 'error';

        if (deletingSelf) {
            await this.rootStore.userStore.checkForAuthenticatedUser();
        }

        return response;
    };


    /*************** Validation Checks ***************/

    isAuthorizedToUpdate(organizationId: number) {
        const userStore = this.rootStore.userStore;
        return userStore.isAuthenticatedOrganization(organizationId);
    }
}


